/**
 * Very simple local storage wrapper with expires support.
 *
 * DO NOT USE FOR
 * - sensitive data
 * - anything that should be server side setting
 */

const CACHE_PREFIX = 'fb-'

export type CachePutOptions = {
  key: string
  value: any
  ttl?: number
  prevKey?: string
}

export function cacheRemove(key: string): void {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`)
  } catch (e) {
    // error saving, user doesn't  have local storage enabled, etc
    console.error('Error removing from localStorage', e)
  }
}

/**
 * ttl is in seconds
 */
export function cachePut({ key, value, ttl = 0, prevKey = '' }: CachePutOptions): void {
  try {
    // remove any previous cache
    if (prevKey) {
      cacheRemove(prevKey)
    }

    // does not expire
    const cacheKey = `${CACHE_PREFIX}${key}`

    if (ttl === 0) {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          value,
          expires: null,
        }),
      )
      return
    }

    const expiresSeconds = Math.floor(Date.now() / 1000 + ttl)

    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        value,
        expires: expiresSeconds,
      }),
    )
  } catch (e) {
    // error saving, user doesn't  have local storage enabled, etc
    console.error('Error saving to local storage', e)
  }
}

/**
 * Return local storage data by key (or return the default value).
 *
 * Generic Type based on the default value.
 */
export function cacheGet<Type>({ key, defaultValue }: { key: string; defaultValue: Type }): Type {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`
    const cache = localStorage.getItem(cacheKey)

    if (!cache) {
      return defaultValue
    }

    const { value, expires } = JSON.parse(cache)

    // check if expired and remove key
    if (expires && expires !== 0 && expires < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(cacheKey)
      return defaultValue
    }

    // cached value
    return value
  } catch (e) {
    // error saving, user doesn't  have local storage enabled, etc
    console.error('Error getting from localStorage', e)
    return defaultValue
  }
}
